import React, { useState, useEffect, useRef } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import {
  Upload,
  FileText,
  CheckCircle,
  Building,
  Calendar,
  DollarSign,
  Plus,
  X,
} from 'lucide-react';
import { trpc } from '@lib/trpc';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ThemeProvider } from '@mui/material/styles';
import { muiTheme } from '../../theme/muiTheme';
import { SupplierRecord } from '../../types';
import { SupplierAutoComplete } from './SupplierAutoComplete';

// Constants
const MAX_PDF_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const MAX_PDF_SIZE_LABEL = '10MB';
const MODAL_MAX_WIDTH_CLASS = 'max-w-7xl';
const MODAL_MAX_HEIGHT_CLASS = 'max-h-[90vh]';
const PDF_PREVIEW_MAX_HEIGHT_CLASS = 'max-h-[calc(90vh-180px)]';

export const ManualInvoiceCreate: React.FC = () => {
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierRecord | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const [formData, setFormData] = useState({
    supplier: '',
    invoiceNumber: '',
    invoiceDate: '',
    totalAmount: '',
    dueDate: '',
    supplierAbn: '',
    notes: '',
  });

  // Fetch suppliers for autocomplete
  const { data: suppliersData, isLoading: isLoadingSuppliers } = trpc.supplier.getAll.useQuery();

  // Handle supplier selection change
  const handleSupplierChange = (supplier: SupplierRecord | null) => {
    setSelectedSupplier(supplier);
    if (supplier) {
      setFormData(prev => ({
        ...prev,
        supplier: supplier.name,
        supplierAbn: supplier.abn || '',
      }));
    } else {
      // Clear supplier fields when selection is cleared
      setFormData(prev => ({
        ...prev,
        supplier: '',
        supplierAbn: '',
      }));
    }
  };

  // Helper to check if there are unsaved changes
  const hasUnsavedChanges = !!pdfFile || Object.values(formData).some(value => value !== '');

  // Cleanup preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const uploadPdfManualMutation = trpc.invoice.uploadPdfManual.useMutation({
    onSuccess: data => {
      toast.success('Invoice created successfully!');
      // Navigate to invoice edit page
      if (data.invoice?.id) {
        navigate(`/invoices/${data.invoice.id}/edit`);
      }
      // Close modal and clear state after navigation
      setIsModalOpen(false);
      handleClearFile();
      setFormData({
        supplier: '',
        invoiceNumber: '',
        invoiceDate: '',
        totalAmount: '',
        dueDate: '',
        supplierAbn: '',
        notes: '',
      });
      setSelectedSupplier(null);
    },
    onError: error => {
      toast.error(error.message || 'Failed to create invoice. Please try again.');
    },
  });

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setPdfFile(file);

      // Generate preview URL and cleanup old one
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
      const newPreviewUrl = URL.createObjectURL(file);
      previewUrlRef.current = newPreviewUrl;
      setPreviewUrl(newPreviewUrl);

      // Open modal after file selection
      setIsModalOpen(true);
    }
  };

  const requestCloseModal = () => {
    if (!hasUnsavedChanges) {
      // No unsaved changes, just close modal
      setIsModalOpen(false);
      return;
    }

    // Check with user before closing
    if (window.confirm('Discard changes?')) {
      // Clear all state
      handleClearFile();
      setFormData({
        supplier: '',
        invoiceNumber: '',
        invoiceDate: '',
        totalAmount: '',
        dueDate: '',
        supplierAbn: '',
        notes: '',
      });
      setSelectedSupplier(null);
      setIsModalOpen(false);
    }
  };

  const handleModalBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking on the backdrop, not the panel
    if (e.target === e.currentTarget) {
      requestCloseModal();
    }
  };

  const handleEscapeKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isModalOpen) {
      requestCloseModal();
    }
  };

  // Register ESC key handler
  useEffect(() => {
    window.addEventListener('keydown', handleEscapeKey);
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isModalOpen]);

  // Helper to convert file to base64 (stripping data URL prefix)
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Strip the "data:application/pdf;base64," prefix
        const base64String = result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle rejected file drops
  const onDropRejected = (rejectedFiles: FileRejection[]) => {
    rejectedFiles.forEach(rejection => {
      const { errors, file } = rejection;
      errors.forEach(error => {
        if (error.code === 'file-too-large') {
          toast.error(`File size exceeds ${MAX_PDF_SIZE_LABEL}. Please upload a smaller file.`);
        } else if (error.code === 'file-invalid-type') {
          toast.error(`Invalid file type: ${file.type}. Only PDF files are supported.`);
        } else {
          toast.error(`Error uploading file: ${error.message}`);
        }
      });
    });
  };

  const handleClearFile = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPdfFile(null);
    setPreviewUrl(null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    maxSize: MAX_PDF_SIZE_BYTES,
    accept: {
      'application/pdf': ['.pdf'],
    },
    multiple: false,
    disabled: isUploading,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!pdfFile) {
      toast.error('Please upload a PDF file');
      return;
    }
    if (
      !formData.supplier ||
      !formData.invoiceNumber ||
      !formData.invoiceDate ||
      !formData.totalAmount
    ) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate totalAmount is a valid number
    const parsedAmount = parseFloat(formData.totalAmount);
    if (isNaN(parsedAmount)) {
      toast.error('Total Amount must be a valid number');
      return;
    }

    // Validate date range: due_date must be on or after invoice_date
    if (formData.dueDate && formData.invoiceDate) {
      if (formData.dueDate < formData.invoiceDate) {
        toast.error('Due Date must be on or after Invoice Date');
        return;
      }
    }

    setIsUploading(true);

    try {
      // Convert file to base64 string
      const base64String = await fileToBase64(pdfFile);

      await uploadPdfManualMutation.mutateAsync({
        file: base64String,
        filename: pdfFile.name,
        supplier: formData.supplier,
        invoice_number: formData.invoiceNumber,
        invoice_date: formData.invoiceDate,
        total_amount: formData.totalAmount,
        due_date: formData.dueDate || undefined,
        supplier_abn: formData.supplierAbn || undefined,
        notes: formData.notes || undefined,
        allowDuplicates: false,
      });
    } catch (error) {
      // Error is handled by the mutation
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* PDF Upload Section - Always Visible */}
      <div className="w-full h-full">
        <div
          {...getRootProps()}
          className={`
            border-4 border-dashed rounded-lg shadow-lg p-8 text-center cursor-pointer transition-all duration-200 min-h-[320px] flex flex-col justify-center
            ${
              isDragActive
                ? 'border-accent-500 bg-accent-50'
                : 'border-secondary-300 hover:border-accent-400'
            }
            ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <input {...getInputProps()} />

          <div className="flex flex-col items-center space-y-4">
            {pdfFile ? (
              <>
                <CheckCircle className="w-12 h-12 text-success-500" />
                <div>
                  <h3 className="text-lg font-semibold text-secondary-800 mb-2">PDF Uploaded</h3>
                  <p className="text-secondary-600">{pdfFile.name}</p>
                </div>
              </>
            ) : isUploading ? (
              <div className="w-12 h-12 border-4 border-accent-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Upload className="w-12 h-12 text-secondary-400" />
                <div>
                  <h3 className="text-lg font-semibold text-secondary-800 mb-2">
                    Upload PDF Invoice
                  </h3>
                  <p className="text-secondary-600">
                    Drag and drop a PDF file here, or click to select
                  </p>
                </div>
                <div className="flex items-center space-x-2 text-sm text-secondary-500">
                  <FileText className="w-4 h-4" />
                  <span>Only PDF files are supported</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal Overlay - Form and Preview */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-secondary-900/50 backdrop-blur-sm"
          onClick={handleModalBackdropClick}
        >
          {/* Modal Panel */}
          <div
            className={`relative bg-white rounded-lg shadow-2xl w-full ${MODAL_MAX_WIDTH_CLASS} ${MODAL_MAX_HEIGHT_CLASS} overflow-auto m-4`}
            onClick={e => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={requestCloseModal}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-secondary-100 transition-colors z-10"
              aria-label="Close modal"
            >
              <X className="w-6 h-6 text-secondary-600" />
            </button>

            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-secondary-200">
              <h2 className="text-xl font-semibold text-secondary-800">Create Manual Invoice</h2>
              <p className="text-secondary-600 text-sm mt-1">
                Fill in the invoice details and submit
              </p>
            </div>

            {/* Modal Body - Form and Preview */}
            <div className="p-6">
              <ThemeProvider theme={muiTheme}>
                <div className="grid grid-cols-1 md:grid-cols-[3fr_7fr] gap-6">
                  {/* Left Column - Form */}
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                      {/* Supplier (Required) */}
                      <div className="space-y-2">
                        <label className="flex items-center space-x-2 text-sm font-medium text-secondary-700">
                          <Building className="w-4 h-4" />
                          <span>Supplier *</span>
                        </label>
                        <SupplierAutoComplete
                          value={selectedSupplier}
                          onChange={handleSupplierChange}
                          disabled={isUploading}
                          currentSupplierName={formData.supplier}
                          currentSupplierAbn={formData.supplierAbn}
                          suppliersData={suppliersData}
                          isLoadingSuppliers={isLoadingSuppliers}
                        />
                      </div>

                      {/* Invoice Number (Required) */}
                      <div className="space-y-2">
                        <label
                          htmlFor="invoiceNumber"
                          className="flex items-center space-x-2 text-sm font-medium text-secondary-700"
                        >
                          <FileText className="w-4 h-4" />
                          <span>Invoice Number *</span>
                        </label>
                        <input
                          type="text"
                          id="invoiceNumber"
                          name="invoiceNumber"
                          value={formData.invoiceNumber}
                          onChange={handleInputChange}
                          required
                          disabled={isUploading}
                          className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="Enter invoice number"
                        />
                      </div>

                      {/* Invoice Date (Required) */}
                      <div className="space-y-2">
                        <label
                          htmlFor="invoiceDate"
                          className="flex items-center space-x-2 text-sm font-medium text-secondary-700"
                        >
                          <Calendar className="w-4 h-4" />
                          <span>Invoice Date *</span>
                        </label>
                        <input
                          type="date"
                          id="invoiceDate"
                          name="invoiceDate"
                          value={formData.invoiceDate}
                          onChange={handleInputChange}
                          required
                          disabled={isUploading}
                          className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </div>

                      {/* Total Amount (Required) */}
                      <div className="space-y-2">
                        <label
                          htmlFor="totalAmount"
                          className="flex items-center space-x-2 text-sm font-medium text-secondary-700"
                        >
                          <DollarSign className="w-4 h-4" />
                          <span>Total Amount *</span>
                        </label>
                        <input
                          type="text"
                          id="totalAmount"
                          name="totalAmount"
                          value={formData.totalAmount}
                          onChange={handleInputChange}
                          required
                          disabled={isUploading}
                          className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="e.g., 100.00"
                        />
                      </div>

                      {/* Supplier ABN (Optional) */}
                      <div className="space-y-2">
                        <label
                          htmlFor="supplierAbn"
                          className="flex items-center space-x-2 text-sm font-medium text-secondary-700"
                        >
                          <Building className="w-4 h-4" />
                          <span>Supplier ABN (Optional)</span>
                        </label>
                        <input
                          type="text"
                          id="supplierAbn"
                          name="supplierAbn"
                          value={formData.supplierAbn}
                          onChange={handleInputChange}
                          disabled={isUploading}
                          className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="Enter ABN"
                        />
                      </div>

                      {/* Due Date (Optional) */}
                      <div className="space-y-2">
                        <label
                          htmlFor="dueDate"
                          className="flex items-center space-x-2 text-sm font-medium text-secondary-700"
                        >
                          <Calendar className="w-4 h-4" />
                          <span>Due Date (Optional)</span>
                        </label>
                        <input
                          type="date"
                          id="dueDate"
                          name="dueDate"
                          value={formData.dueDate}
                          onChange={handleInputChange}
                          disabled={isUploading}
                          className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </div>
                    </div>

                    {/* Notes (Optional) */}
                    <div className="space-y-2">
                      <label htmlFor="notes" className="text-sm font-medium text-secondary-700">
                        Notes (Optional)
                      </label>
                      <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        disabled={isUploading}
                        rows={3}
                        className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="Add any additional notes"
                      />
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end pt-2">
                      <button
                        type="submit"
                        disabled={isUploading || !pdfFile}
                        className="flex items-center space-x-2 px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isUploading ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <span>Processing...</span>
                          </>
                        ) : (
                          <>
                            <Plus className="w-5 h-5" />
                            <span>Create Invoice</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>

                  {/* Right Column - PDF Preview */}
                  <div
                    className={`relative border border-secondary-300 rounded-lg bg-secondary-50 p-4 min-h-[900px] ${PDF_PREVIEW_MAX_HEIGHT_CLASS} overflow-auto`}
                  >
                    {previewUrl && pdfFile ? (
                      <>
                        <button
                          type="button"
                          onClick={handleClearFile}
                          className="absolute top-2 right-2 p-1 rounded-md bg-white border border-secondary-300 hover:bg-secondary-100 transition-colors z-10"
                          aria-label="Clear PDF"
                        >
                          <X className="w-4 h-4 text-secondary-600" />
                        </button>
                        <iframe
                          src={previewUrl}
                          className="w-full h-full rounded-md"
                          title="PDF Preview"
                        />
                      </>
                    ) : (
                      <div className="h-full flex items-center justify-center">
                        <div className="text-center">
                          <FileText className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
                          <p className="text-secondary-500 text-lg font-medium">
                            PDF preview will appear here
                          </p>
                          <p className="text-secondary-400 text-sm mt-2">
                            Upload a PDF to see the preview
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </ThemeProvider>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
